import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight,
    FolderOpen,
    ChevronsDown,
    BrainCircuit,
    Cloud,
    Layers,
    ShieldCheck,
} from 'lucide-react';

const ROLES = ['software.', 'AI systems.', 'cloud platforms.', 'ideas that ship.'];

class Landing extends Component {
    constructor(props){
        super(props);
        this.state = { roleIndex: 0, typed: '', phase: 'typing' };
        this.handleClick = this.handleClick.bind(this);
    }

    componentDidMount(){
        window.Pts.quickStart('#pt', '#0b1220');

        function DeviceCheck(){
            var check = false;
            (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
            return check;
        };
        if(DeviceCheck()){
            const el = document.getElementById('pt');
            if (el) el.style.pointerEvents = 'none';
        }

        (function() {
            // ── Neural Field ────────────────────────────────────────────
            // Three concentric layers of nodes (core, inner ring, outer ring)
            // with traveling data pulses and a rhythmic heartbeat ring.
            var core = [];
            var particles = [];
            var outerParticles = [];
            var pulses = []; // data packets traveling along the outer ring
            const PCOUNT = 220;
            const PCOUNT_OUTER = 64;
            const PCOUNT_CORE = 40;
            const PULSE_COUNT = 10;

            window.space.add({
                start: (bound) => {
                    // Store radii as FRACTIONS of min(width,height) rather than
                    // absolute pixels. animate() multiplies by the current dim
                    // every frame, so particles adapt when the canvas resizes
                    // — fixes the "tiny cluster" state after refreshing on a
                    // non-home route (ScrollManager jumps to innerHeight while
                    // Pts is still measuring, so start() sees the wrong dim).
                    const c = window.space.center;
                    const dim = Math.min(window.space.width, window.space.height);

                    for (let i = 0; i < PCOUNT_CORE; i++) {
                        const ang = (Math.PI * 2 * i) / PCOUNT_CORE;
                        const radFrac = 0.45 * (0.12 + 0.14 * Math.random());
                        const rr = radFrac * dim;
                        const p = new window.Pt(c.x + Math.cos(ang) * rr, c.y + Math.sin(ang) * rr);
                        p._ang = ang; p._radFrac = radFrac;
                        core.push(p);
                    }
                    for (let i = 0; i < PCOUNT; i++) {
                        const ang = (Math.PI * 2 * i) / PCOUNT;
                        const radFrac = 0.45 * (0.45 + 0.55 * Math.random());
                        const rr = radFrac * dim;
                        const p = new window.Pt(c.x + Math.cos(ang) * rr, c.y + Math.sin(ang) * rr);
                        p._ang = ang; p._radFrac = radFrac;
                        particles.push(p);
                    }
                    for (let i = 0; i < PCOUNT_OUTER; i++) {
                        const ang = (Math.PI * 2 * i) / PCOUNT_OUTER;
                        const radFrac = 0.45 * (0.85 + 0.12 * (Math.random() - 0.5));
                        const rr = radFrac * dim;
                        const p = new window.Pt(c.x + Math.cos(ang) * rr, c.y + Math.sin(ang) * rr);
                        p._ang = ang; p._radFrac = radFrac;
                        outerParticles.push(p);
                    }
                    for (let i = 0; i < PULSE_COUNT; i++) {
                        pulses.push({
                            seg: Math.floor(Math.random() * PCOUNT_OUTER),
                            t: Math.random(),
                            speed: 0.006 + Math.random() * 0.01,
                            hue: Math.random() < 0.5 ? 'cyan' : 'white',
                        });
                    }
                },

                animate: (time, ftime) => {
                    const t = time * 0.001;
                    const mouse = window.space.pointer || window.space.center;
                    const rot = 0.12 * Math.sin(t * 0.5);
                    const rotOuter = 0.06 * Math.cos(t * 0.35 + 0.2);
                    const rotCore = -0.18 * Math.sin(t * 0.7);
                    const dim = Math.min(window.space.width, window.space.height);

                    // ── heartbeat rings (expanding & fading) ────────────
                    const beat = (t % 3) / 3; // 0 → 1 over 3s
                    const beatR = Math.min(window.space.width, window.space.height) * 0.5 * beat;
                    const beatA = 0.22 * (1 - beat);
                    if (beatA > 0.01) {
                        window.form.strokeOnly(`rgba(33,194,200,${beatA})`, 1.2)
                            .circle([window.space.center, [beatR, beatR]]);
                    }
                    const beat2 = ((t + 1.5) % 3) / 3;
                    const beat2R = Math.min(window.space.width, window.space.height) * 0.5 * beat2;
                    const beat2A = 0.14 * (1 - beat2);
                    if (beat2A > 0.01) {
                        window.form.strokeOnly(`rgba(255,255,255,${beat2A})`, 0.8)
                            .circle([window.space.center, [beat2R, beat2R]]);
                    }

                    // ── core cluster (dense, near center) ───────────────
                    for (let i = 0, len = core.length; i < len; i++) {
                        let p = core[i];
                        const wob = 3 * Math.sin(t * 1.8 + i * 0.3);
                        const ang = p._ang + rotCore;
                        const rr = p._radFrac * dim + wob;
                        const cx = window.space.center.x + Math.cos(ang) * rr;
                        const cy = window.space.center.y + Math.sin(ang) * rr;
                        p.x += (cx - p.x) * 0.18;
                        p.y += (cy - p.y) * 0.18;

                        const pulse = 0.55 + 0.45 * Math.sin(t * 2.4 + i * 0.35);
                        window.form.fillOnly(`rgba(33,194,200,${0.35 * pulse})`).point(p, 1.6 + pulse);

                        // link core to next core for a tight web
                        const next = core[(i + 1) % len];
                        window.form.stroke(`rgba(33,194,200,${0.10 + 0.12 * pulse})`, 0.6).line([p, next]);
                        // occasional spoke across the core
                        if (i % 4 === 0) {
                            const across = core[(i + Math.floor(len / 2)) % len];
                            window.form.stroke(`rgba(33,194,200,${0.05 + 0.08 * pulse})`, 0.4).line([p, across]);
                        }
                    }

                    // ── inner ring particles ────────────────────────────
                    for (let i = 0, len = particles.length; i < len; i++) {
                        let p = particles[i];
                        const wob = 8 * Math.sin(t * 1.2 + i * 0.14) + 6 * Math.cos(t * 0.6 + i * 0.07);
                        const ang = p._ang + rot + 0.002 * i;
                        const rr = p._radFrac * dim + wob;
                        const cx = window.space.center.x + Math.cos(ang) * rr;
                        const cy = window.space.center.y + Math.sin(ang) * rr;
                        p.x += (cx - p.x) * 0.12;
                        p.y += (cy - p.y) * 0.12;

                        const d = Math.sqrt((p.x - mouse.x) ** 2 + (p.y - mouse.y) ** 2);
                        const maxD = Math.max(200, Math.min(window.space.width, window.space.height) * 0.6);
                        const influence = Math.max(0, 1 - d / maxD);
                        const alpha = 0.12 + 0.7 * influence;

                        const next = particles[(i + 1) % len];
                        window.form.stroke(`rgba(33,194,200,${0.06 + 0.28 * influence})`, 0.9).line([p, next]);
                        window.form.stroke(`rgba(255,255,255,${0.02 + 0.06 * influence})`, 0.6).line([p, window.space.center]);
                        const size = 1 + 6 * Math.pow(influence, 1.6);
                        window.form.fillOnly(`rgba(33,194,200,${alpha})`).point(p, size);
                    }

                    // ── outer ring particles ────────────────────────────
                    for (let i = 0, lenO = outerParticles.length; i < lenO; i++) {
                        let p = outerParticles[i];
                        const wob = 14 * Math.sin(t * 0.7 + i * 0.18) + 6 * Math.cos(t * 0.45 + i * 0.09);
                        const ang = p._ang + rotOuter + 0.0015 * i;
                        const rr = p._radFrac * dim + wob * 0.4;
                        const cx = window.space.center.x + Math.cos(ang) * rr;
                        const cy = window.space.center.y + Math.sin(ang) * rr;
                        p.x += (cx - p.x) * 0.08;
                        p.y += (cy - p.y) * 0.08;

                        const d = Math.sqrt((p.x - mouse.x) ** 2 + (p.y - mouse.y) ** 2);
                        const maxD = Math.max(240, Math.min(window.space.width, window.space.height) * 0.7);
                        const influence = Math.max(0, 1 - d / maxD);

                        window.form.stroke(`rgba(255,255,255,${0.06 + 0.5 * influence})`, 1.2).line([p, outerParticles[(i + 1) % lenO]]);
                        window.form.stroke(`rgba(255,255,255,${0.02 + 0.22 * influence})`, 0.9).line([p, outerParticles[(i + 3) % lenO]]);
                        const osize = 2 + 5 * Math.pow(influence, 1.4);
                        window.form.fillOnly(`rgba(255,255,255,${0.06 + 0.7 * influence})`).point(p, osize);

                        // link outer to a few inner particles
                        const sample = 6;
                        for (let s = 0; s < sample; s++) {
                            const idx = Math.floor((i * (PCOUNT / lenO) + s * 7) % PCOUNT);
                            const ip = particles[idx];
                            const dist = Math.sqrt((p.x - ip.x) ** 2 + (p.y - ip.y) ** 2);
                            if (dist < 160) {
                                const aa = 0.02 + 0.35 * (1 - dist / 160);
                                window.form.stroke(`rgba(255,255,255,${aa})`, 0.6).line([p, ip]);
                            }
                        }
                    }

                    // ── data pulses traveling along the outer ring ──────
                    for (let i = 0; i < pulses.length; i++) {
                        let pu = pulses[i];
                        pu.t += pu.speed;
                        while (pu.t >= 1) { pu.t -= 1; pu.seg = (pu.seg + 1) % outerParticles.length; }
                        const a = outerParticles[pu.seg];
                        const b = outerParticles[(pu.seg + 1) % outerParticles.length];
                        const x = a.x + (b.x - a.x) * pu.t;
                        const y = a.y + (b.y - a.y) * pu.t;
                        const tail = 8;
                        // trailing streak
                        for (let k = 0; k < tail; k++) {
                            const tt = pu.t - k * 0.06;
                            let seg = pu.seg;
                            let ttt = tt;
                            while (ttt < 0) { ttt += 1; seg = (seg - 1 + outerParticles.length) % outerParticles.length; }
                            const aa = outerParticles[seg];
                            const bb = outerParticles[(seg + 1) % outerParticles.length];
                            const tx = aa.x + (bb.x - aa.x) * ttt;
                            const ty = aa.y + (bb.y - aa.y) * ttt;
                            const fade = (1 - k / tail);
                            const col = pu.hue === 'cyan'
                                ? `rgba(33,194,200,${0.55 * fade})`
                                : `rgba(255,255,255,${0.55 * fade})`;
                            window.form.fillOnly(col).point([tx, ty], 1.8 * fade + 0.4);
                        }
                        // bright head
                        const headCol = pu.hue === 'cyan'
                            ? 'rgba(120,240,245,0.95)'
                            : 'rgba(255,255,255,0.95)';
                        window.form.fillOnly(headCol).point([x, y], 3.2);
                    }
                }
            });
            window.space.bindMouse().bindTouch().play();
        })();

        // Force Pts to re-measure once layout has settled. Fixes the case
        // where the canvas backing store gets sized wrong on refresh at a
        // non-home route (ScrollManager jumps to innerHeight while layout is
        // still racing), leaving a small/pixelated canvas when scrolled back up.
        this.resizeTimers = [
            requestAnimationFrame(() => window.dispatchEvent(new Event('resize'))),
            setTimeout(() => window.dispatchEvent(new Event('resize')), 120),
            setTimeout(() => window.dispatchEvent(new Event('resize')), 500),
        ];

        // ── typing / role-morph loop ────────────────────────────────
        const step = () => {
            const { roleIndex, typed, phase } = this.state;
            const word = ROLES[roleIndex];
            if (phase === 'typing') {
                if (typed.length < word.length) {
                    this.setState({ typed: word.slice(0, typed.length + 1) });
                    this.typeTimer = setTimeout(step, 70);
                } else {
                    this.setState({ phase: 'pause' });
                    this.typeTimer = setTimeout(step, 1600);
                }
            } else if (phase === 'pause') {
                this.setState({ phase: 'deleting' });
                this.typeTimer = setTimeout(step, 40);
            } else {
                if (typed.length > 0) {
                    this.setState({ typed: typed.slice(0, -1) });
                    this.typeTimer = setTimeout(step, 40);
                } else {
                    this.setState({
                        roleIndex: (roleIndex + 1) % ROLES.length,
                        phase: 'typing',
                    });
                    this.typeTimer = setTimeout(step, 300);
                }
            }
        };
        this.typeTimer = setTimeout(step, 900);
    }

    componentWillUnmount(){
        if (this.typeTimer) clearTimeout(this.typeTimer);
        if (this.resizeTimers) {
            cancelAnimationFrame(this.resizeTimers[0]);
            clearTimeout(this.resizeTimers[1]);
            clearTimeout(this.resizeTimers[2]);
        }
        try {
            if (window.space && typeof window.space.dispose === 'function') window.space.dispose();
            else if (window.space && typeof window.space.stop === 'function') window.space.stop();
        } catch (e) { /* ignore */ }
        const ptEl = document.getElementById('pt');
        if (ptEl) ptEl.innerHTML = '';
    }

    handleClick(){
        const landing = document.getElementById('landingPage');
        const target = landing ? landing.getBoundingClientRect().bottom + window.scrollY : window.innerHeight;
        window.scrollTo({ top: target, behavior: 'smooth' });
    }

    render() {
        const { typed } = this.state;
        return (
            <div id="landingPage">
                <div className="hud-corner hud-tl" aria-hidden="true"></div>
                <div className="hud-corner hud-tr" aria-hidden="true"></div>
                <div className="hud-corner hud-bl" aria-hidden="true"></div>
                <div className="hud-corner hud-br" aria-hidden="true"></div>

                <div id="pt"></div>

                <div className="landing-grid">
                    <div id="landingText">
                        <div className="pre-label">
                            <span className="pre-dot"></span>
                            <span>SENIOR SOFTWARE ENGINEER · AI &amp; CLOUD</span>
                        </div>

                        <h1 className="landing-h1">
                            Hello, I'm <span id="landingMyName">Stivan Kitchoukov</span>.
                        </h1>

                        <p className="landing-morph">
                            <span className="morph-prompt">$</span> I build&nbsp;
                            <span className="morph-word">{typed}</span>
                            <span className="morph-caret" aria-hidden="true">▍</span>
                        </p>

                        <div className="landing-ctas">
                            <Link id="landingPage_quote" to="/Contact" onClick={this.handleClick} className="cta-primary">
                                Get Quote
                                <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
                            </Link>
                            <Link to="/Portfolio" className="cta-secondary">
                                Explore Work
                                <FolderOpen size={16} strokeWidth={1.75} aria-hidden="true" />
                            </Link>
                        </div>

                        <div className="landing-chips">
                            <span className="landing-chip"><BrainCircuit size={13} strokeWidth={1.5} /> AI Engineering</span>
                            <span className="landing-chip"><Cloud size={13} strokeWidth={1.5} /> Cloud-Native</span>
                            <span className="landing-chip"><Layers size={13} strokeWidth={1.5} /> Architecture</span>
                            <span className="landing-chip"><ShieldCheck size={13} strokeWidth={1.5} /> Reliability</span>
                        </div>
                    </div>
                </div>

                <button className="scroll-indicator" onClick={this.handleClick} aria-label="Scroll to content">
                    <ChevronsDown size={26} strokeWidth={1.75} />
                </button>
            </div>
        );
    }
}

export default Landing;
